/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { jsx } from '@emotion/core';
import OverlayFactory from 'comps/OverlayFactory';
import { Popover } from 'react-bootstrap';
import MetricsContext from './MetricsContext';
import commands from './commands';
import { useContext, useState, useEffect } from 'react';
import cx from 'classnames';
import type { CartoError } from 'lib/getCartoMap';
import CustomError from 'lib/CustomError';
import NonEmptyArray from 'lib/NonEmptyArray';
import Task from 'folktale/concurrency/task';

const PopoverWrapper = OverlayFactory(Popover);

const Component = (props: { id: string }): Element<string> => {
  const ctx = useContext(MetricsContext);
  const [taskExecution, setExecution] = useState();
  const [errors, setErrors] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const { geojson } = ctx.state;

  // Cancel execution after a period of time. Show an explanation
  // that the data is too large to download.
  useEffect(() => {
    if (!taskExecution) return;
    const timer = setTimeout(() => {
      taskExecution.cancel();
      setErrors(['Data is too large to download.']);
      setExecution();
      setLoading(false);
    }, 8000);
    return () => clearTimeout(timer);
  }, [taskExecution]);

  const popoverContent = geojson
    ? 'Download data'
    : 'Select an area to enable download.';

  return (
    <div className="ml-auto text-right">
      <button
        disabled={Boolean(geojson) === false}
        data-testid="DataDownloadButton"
        className="p-0 mr-1"
        onClick={() => {
          if (taskExecution) return;
          if (!geojson) return;
          setLoading(true);
          const exec = commands(ctx)
            .downloadAreaData(props.id)
            .map(() => {
              setErrors([]);
              setLoading(false);
              setExecution();
            })
            .orElse((e: CustomError<CartoError>) => {
              setLoading(false);
              setExecution();
              setErrors(JSON.parse(e.message).error);
              return Task.task((res) => res.resolve());
            })
            .run();
          setExecution(exec);
        }}
      >
        <PopoverWrapper
          content={popoverContent}
          placement="top"
          id="download-tooltip"
          overrideTestHack
        >
          <i
            data-testid="DownloadIcon"
            className={cx('fa', {
              'fa-download': !isLoading,
              'fa-spinner fa-spin': isLoading,
            })}
          />
        </PopoverWrapper>
      </button>
      {NonEmptyArray.is(errors) && (
        <div data-testid="DownloadErrors">
          {errors.map((v, i) => (
            <div key={i} className="text-danger">
              {v}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Component;
